import React, { createContext, useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'
import TopPanel from './TopPanel'
import SearchBar from './SearchBar'
import Stack from '@mui/material/Stack'
import moment from 'moment'
import { Typography } from '@mui/material'
import SelectedShiftDrawer from './SelectedShiftDrawer'
import PayRuns from './PayRuns/PayRuns'
import StaffInvoices from './StaffInvoices'
import Expenses from './Expenses'
import UnpaidExpenses from './UnpaidExpenses'
import MissingPaymentTypeWarning from './MissingPaymentTypeWarning'

export const PayrollContext = createContext({})

const defaultPayrollSearchParams = {
  startDate: moment().subtract(1, 'month').startOf('month'),
  endDate: moment().subtract(1, 'month').endOf('month'),
  paid: 'Unpaid',
  employee: null,
  shiftType: 'All',
}

const Payroll = ({}) => {
  const { setToast } = useContext(GlobalContext)
  const [searchParams, setSearchParams] = useState(defaultPayrollSearchParams)
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [selectedShift, setSelectedShift] = useState(null)
  const [wagesTotals, setWagesTotals] = useState({})
  const [highlightedShifts, setHighlightedShifts] = useState([])
  const [selectedExpenses, setSelectedExpenses] = useState([])
  const [payRunRefreshKey, setPayRunRefreshKey] = useState(0)
  const [missingPaymentTypeEmployees, setMissingPaymentTypeEmployees] = useState([])
  const [missingPaymentTypesLoading, setMissingPaymentTypesLoading] = useState(false)

  const loadShifts = () => {
    setLoading(true)
    API.post('/shifts-for-payroll', {
      ...searchParams,
      employee: searchParams?.employee?.employee?._id ?? null,
    })
      .then((res) => {
        if (res.status === 200) {
          setResults(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadShifts()
  }, [searchParams])

  useEffect(() => {
    API.get('/payroll-wages-totals')
      .then((res) => {
        if (res.status === 200) {
          setWagesTotals(res.data)
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ?? 'Unable to load payroll wage totals.',
          })
        }
      })
      .catch(() =>
        setToast({
          severity: 'error',
          message: 'Unable to load payroll wage totals.',
        }),
      )
  }, [])

  const loadMissingPaymentTypes = () => {
    setMissingPaymentTypesLoading(true)
    API.get('/payroll/missing-payment-types')
      .then((res) => {
        if (res.status === 200) {
          setMissingPaymentTypeEmployees(res.data)
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ??
              'Unable to check employee payment types.',
          })
        }
      })
      .catch(() =>
        setToast({
          severity: 'error',
          message: 'Unable to check employee payment types.',
        }),
      )
      .finally(() => setMissingPaymentTypesLoading(false))
  }

  useEffect(() => {
    loadMissingPaymentTypes()
  }, [])

  const handleMarkShiftsAsPaid = (shifts) => {
    API.post('/payroll/mark-shifts-as-paid', {
      shifts,
    }).then((res) => {
      if (res.status === 200) {
        loadShifts()
        setHighlightedShifts([])
      } else {
        setToast({
          severity: 'error',
          message:
            res.data?.error ??
            'Unable to mark shifts as paid. Please try again later.',
        })
      }
    })
  }

  const handleCreatePayRun = (shifts, expenses) => {
    API.post('/payroll/pay-runs', {
      shifts,
      expenses,
    })
      .then((res) => {
        if (res.status === 200) {
          loadShifts()
          setHighlightedShifts([])
          setSelectedExpenses([])
          setPayRunRefreshKey((prev) => prev + 1)
        } else {
          setToast({
            severity: 'error',
            message:
              res.data?.error ??
              'Unable to create the pay run. Please try again later.',
          })
        }
      })
      .catch(() =>
        setToast({
          severity: 'error',
          message: 'Unable to create the pay run. Please try again later.',
        }),
      )
  }

  return (
    <PayrollContext.Provider
      value={{
        searchParams,
        setSearchParams,
        defaultPayrollSearchParams,
        results,
        selectedShift,
        setSelectedShift,
        loading,
        fetchError,
        wagesTotals,
        loadShifts,
        handleMarkShiftsAsPaid,
        handleCreatePayRun,
        highlightedShifts,
        setHighlightedShifts,
        selectedExpenses,
        setSelectedExpenses,
        payRunRefreshKey,
      }}
    >
      <Stack gap={1}>
        <Stack flex={1}>
          <Typography variant="title2">Payroll</Typography>
          <Typography variant="body2">
            Manage your staff's payroll activity
          </Typography>
        </Stack>
        <MissingPaymentTypeWarning
          employees={missingPaymentTypeEmployees}
          loading={missingPaymentTypesLoading}
          onSaved={() => {
            loadMissingPaymentTypes()
            loadShifts()
          }}
        />
        <TopPanel />
        <PayRuns
          refreshKey={payRunRefreshKey}
          onChanged={() => {
            loadShifts()
            setPayRunRefreshKey((prev) => prev + 1)
          }}
        />
        <StaffInvoices refreshKey={payRunRefreshKey} />
        <Expenses refreshKey={payRunRefreshKey} />
        <UnpaidExpenses refreshKey={payRunRefreshKey} />
        <SearchBar />
        <SelectedShiftDrawer />
      </Stack>
    </PayrollContext.Provider>
  )
}

export default Payroll
